<template>
    <div id="page-team-insights" data-page="team-insights" class="team-management-page py-4">
        <!-- E2E backward compatibility marker -->
        <div id="page-subordinates" data-page="subordinates" class="position-absolute" style="top:0;left:0;width:1px;height:1px;opacity:0.01;pointer-events:none;"></div>
        <b-container v-if="!isDevEnv" fluid>
            <!-- Page Header (Beta / Production Coming Next State) -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom team-page-header">
                <div>
                    <h3 class="font-weight-bold mb-1 d-flex align-items-center team-title">
                        <i class="bi bi-people-fill text-primary mr-2"></i>Team Insights
                        <b-badge variant="info" class="ml-3 px-2 py-1" style="font-size: 0.72rem; vertical-align: middle;" data-test="badge-team-coming-next">
                            <i class="bi bi-clock-history mr-1"></i>Coming Next
                        </b-badge>
                    </h3>
                    <div class="text-muted small">
                        Squad member profiles, monthly capacity pacing, worktype distribution, and timesheet reminders
                    </div>
                </div>
            </div>

            <!-- Coming Next Hero Card -->
            <div class="team-coming-soon-wrapper py-4" data-test="team-coming-next-card">
                <b-card class="border-0 shadow-sm text-center py-5 px-4 mx-auto team-coming-soon-card" style="max-width: 780px; border-radius: 16px;">
                    <div class="coming-soon-icon-badge mx-auto mb-3 d-flex align-items-center justify-content-center">
                        <i class="bi bi-people-fill text-info" style="font-size: 2.2rem;"></i>
                    </div>
                    <span class="badge badge-pill px-3 py-1 mb-3 font-weight-bold text-uppercase coming-soon-pill">
                        <i class="bi bi-stars mr-1"></i>Coming Next on the Roadmap
                    </span>
                    <h4 class="font-weight-bold mb-2 team-title">Team Insights is Coming Next</h4>
                    <p class="text-muted mx-auto mb-4" style="max-width: 600px; font-size: 0.95rem; line-height: 1.6;">
                        During the Public Beta, VelocyTrack is focused on perfecting the individual <strong>Solo Engineering</strong> workflow. Multi-seat Team workspaces and manager insights are currently in active development and will be unlocked in an upcoming release.
                    </p>

                    <b-row class="text-left mt-4 pt-3 border-top">
                        <b-col md="6" class="mb-3">
                            <div class="d-flex align-items-start p-3 rounded feature-preview-card h-100">
                                <i class="bi bi-person-badge text-primary mr-3 mt-1" style="font-size: 1.35rem;"></i>
                                <div>
                                    <div class="font-weight-bold small mb-1 feature-preview-title">Member Profiles &amp; Timesheet Insights</div>
                                    <div class="text-muted" style="font-size: 0.8rem;">Track monthly logged hours, daily velocity sparklines, and activity breakdowns across your engineering squad.</div>
                                </div>
                            </div>
                        </b-col>
                        <b-col md="6" class="mb-3">
                            <div class="d-flex align-items-start p-3 rounded feature-preview-card h-100">
                                <i class="bi bi-bell text-warning mr-3 mt-1" style="font-size: 1.35rem;"></i>
                                <div>
                                    <div class="font-weight-bold small mb-1 feature-preview-title">Automated Timesheet Nudges</div>
                                    <div class="text-muted" style="font-size: 0.8rem;">Send dual-channel email reminders and in-app dashboard banners to squad members behind target.</div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </b-card>
            </div>
        </b-container>

        <!-- Missing Team Space Warning Card -->
        <card-missing-source
            v-else-if="!availableWorkspaces.length && !loadingAll"
            source-type="team"
            feature-name="Team"
            data-test="banner-no-team-space"
        />

        <!-- Service Unavailable Placeholder -->
        <div v-else-if="isServiceUnavailable" class="text-center py-5 text-muted">
            <i class="bi bi-cloud-slash fa-2x mb-2 d-block text-warning"></i>
            <div class="font-weight-bold mb-1">Service is currently unreachable</div>
            <span class="small">Team insights will be loaded automatically once the service is restored.</span>
        </div>

        <b-container v-else fluid>
            <!-- Page Header -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom team-page-header">
                <div>
                    <div class="d-flex align-items-center flex-wrap">
                        <h3 class="font-weight-bold mb-1 mr-3 team-title">
                            <i class="bi bi-person-badge text-primary mr-2"></i>Team Insights
                        </h3>
                        <b-badge :variant="isCurrentTeamAdmin ? 'primary' : 'secondary'" class="px-2 py-1 mr-2 mb-1" data-test="badge-current-team-role">
                            <i class="bi bi-shield-check mr-1"></i>{{ currentTeamRoleLabel }}
                        </b-badge>
                        <b-badge v-if="activeTeamObj" variant="info" class="px-2 py-1 mb-1" data-test="badge-team-seat-summary">
                            <i class="bi bi-layers-fill mr-1"></i>{{ activeTeamObj.activeMembersCount ?? 0 }} / {{ activeTeamObj.seatLimit || 5 }} Seats
                        </b-badge>
                    </div>
                    <div class="text-muted small">
                        Squad member profiles, monthly capacity pacing, worktype distribution, and timesheet reminders across shared Project Spaces
                    </div>
                </div>

                <!-- Team Switcher, Shared Project Space Filter & Manage Squad CTA -->
                <div class="d-flex flex-wrap align-items-center mt-3 mt-lg-0">
                    <div class="d-flex align-items-center mr-2 mb-2 mb-sm-0" v-if="availableWorkspaces.length">
                        <label class="small font-weight-bold text-muted text-uppercase mr-2 mb-0">Team:</label>
                        <b-form-select
                            v-model="selectedWorkspaceId"
                            size="sm"
                            style="min-width: 185px;"
                            data-test="select-active-team"
                            @change="onTeamSelectionChange"
                        >
                            <b-form-select-option
                                v-for="ws in availableWorkspaces"
                                :key="ws.workspaceId"
                                :value="ws.workspaceId"
                            >
                                {{ ws.name }} ({{ formatTeamRole(ws.currentUserRole) }})
                            </b-form-select-option>
                        </b-form-select>
                    </div>

                    <div class="d-flex align-items-center mr-2 mb-2 mb-sm-0">
                        <label class="small font-weight-bold text-muted text-uppercase mr-2 mb-0">Project Space:</label>
                        <b-form-select
                            v-model="selectedProjectSpaceId"
                            size="sm"
                            style="min-width: 200px;"
                            data-test="select-team-project-space"
                            @change="onProjectSpaceSelectionChange"
                        >
                            <b-form-select-option :value="null">All Shared Project Spaces</b-form-select-option>
                            <b-form-select-option
                                v-for="sp in activeTeamProjectSpaces"
                                :key="sp.id || sp.templateProjectSpaceId"
                                :value="sp.memberProjectSpaceId || sp.templateProjectSpaceId || sp.id"
                            >
                                {{ sp.projectSpaceName }}
                            </b-form-select-option>
                        </b-form-select>
                    </div>

                    <b-button
                        variant="outline-primary"
                        size="sm"
                        class="font-weight-bold mb-2 mb-sm-0"
                        data-test="btn-goto-manage-team"
                        @click="goToManageTeams"
                    >
                        <i class="bi bi-gear-fill mr-1"></i>Manage Squad &amp; Invites
                    </b-button>
                </div>
            </div>

            <!-- MEMBER PROFILES & INSIGHTS CARD -->
            <b-card no-body class="shadow-sm border mb-4 config-section-card" id="section-team-profiles">
                <div class="card-header border-bottom py-3 px-4 d-flex flex-wrap justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                        <div class="section-icon mr-2 text-primary">
                            <i class="bi bi-person-badge fa-lg"></i>
                        </div>
                        <div>
                            <h5 class="font-weight-bold text-dark mb-0 d-flex align-items-center">
                                Member Profiles &amp; Insights
                                <b-badge pill variant="primary" class="ml-2 px-2 font-weight-bold">{{ subordinates.length }}</b-badge>
                            </h5>
                            <div class="small text-muted">
                                Track monthly logged hours, daily velocity sparklines, and activity breakdowns across your engineering squad.
                            </div>
                        </div>
                    </div>
                    <div class="d-flex flex-wrap align-items-center mt-2 mt-md-0">
                        <b-button
                            v-if="isCurrentTeamAdmin && membersBehindList.length > 0"
                            variant="warning"
                            size="sm"
                            class="mr-3 font-weight-bold"
                            data-test="btn-nudge-all-behind"
                            @click="openNudgeModal(null, true)"
                        >
                            <i class="bi bi-bell-fill mr-1"></i>Remind All Behind ({{ membersBehindList.length }})
                        </b-button>

                        <div class="d-flex align-items-center">
                            <b-button variant="outline-secondary" size="sm" class="mr-2" @click="navigateMonths(-1)" title="Previous month">
                                &laquo; Previous
                            </b-button>
                            <div class="team-month-label mx-2 font-weight-bold">{{ monthLabel }}</div>
                            <b-button variant="outline-secondary" size="sm" class="ml-2" @click="navigateMonths(1)" :disabled="isCurrentMonthDisplayed" title="Next month">
                                Next &raquo;
                            </b-button>
                        </div>
                    </div>
                </div>

                <div class="card-body p-4 team-profiles-section" data-test="tab-team-timesheets">
                    <!-- Role Transparency Notice for Standard Members -->
                    <b-alert
                        v-if="!isCurrentTeamAdmin"
                        show
                        variant="info"
                        class="mb-3 d-flex align-items-center justify-content-between flex-wrap"
                        data-test="alert-role-transparency"
                    >
                        <div>
                            <i class="bi bi-shield-lock-fill mr-2"></i>
                            <strong>Role-Aware Privacy View:</strong> As a squad <strong>{{ currentTeamRoleLabel }}</strong>, this page shows your own timesheet summary. Full squad deliverable balancing, capacity burn-down, and leave schedules are available to everyone on the <strong>Team Dashboard</strong>.
                        </div>
                    </b-alert>

                    <!-- Search by member / role -->
                    <div class="d-flex align-items-center mb-4" style="max-width: 320px; width: 100%;">
                        <b-input-group size="sm">
                            <template #prepend>
                                <b-input-group-text><i class="bi bi-search"></i></b-input-group-text>
                            </template>
                            <b-form-input
                                v-model="memberSearchQuery"
                                placeholder="Search members or roles..."
                                debounce="200"
                                data-test="input-search-members"
                            ></b-form-input>
                            <template #append v-if="memberSearchQuery">
                                <b-button size="sm" variant="outline-secondary" @click="memberSearchQuery = ''">
                                    <i class="bi bi-x"></i>
                                </b-button>
                            </template>
                        </b-input-group>
                    </div>

                    <!-- Summary KPI Strip for Profiles -->
                    <b-row class="mb-4" v-if="subordinates.length">
                        <b-col md="3" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Squad Size</div>
                                <div class="display-4 font-weight-bold text-dark my-1">{{ subordinates.length }}</div>
                                <div class="small text-muted">Active engineers &amp; specialists</div>
                            </b-card>
                        </b-col>
                        <b-col md="3" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Expected Budget</div>
                                <div class="display-4 font-weight-bold text-primary my-1">{{ profilesSummary.totalExpected }}<span class="h4 font-weight-normal text-muted">h</span></div>
                                <div class="small text-muted">Target across active squad</div>
                            </b-card>
                        </b-col>
                        <b-col md="3" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Logged to Date</div>
                                <div class="display-4 font-weight-bold text-success my-1">{{ profilesSummary.totalLogged }}<span class="h4 font-weight-normal text-muted">h</span></div>
                                <div class="small text-muted">Work: {{ profilesSummary.workHours }}h &bull; Act: {{ profilesSummary.activityHours }}h</div>
                            </b-card>
                        </b-col>
                        <b-col md="3" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Squad Pace</div>
                                <div class="display-4 font-weight-bold text-info my-1">{{ profilesSummary.completionPct }}%</div>
                                <b-progress :value="profilesSummary.completionPct" :max="100" variant="info" height="6px" class="rounded"></b-progress>
                            </b-card>
                        </b-col>
                    </b-row>

                    <!-- Members List / Loading State -->
                    <b-overlay :show="loadingSubordinates" rounded="lg" opacity="0.6">
                        <b-alert v-if="!loadingSubordinates && !filteredSubordinates.length" show variant="secondary" class="text-center py-4">
                            <i class="bi bi-info-circle mr-2 h5 mb-0"></i>No team members found matching your search.
                        </b-alert>

                        <b-card
                            v-for="sub in filteredSubordinates"
                            :key="sub.accountUid"
                            class="team-member-card mb-3 shadow-sm border-0"
                            data-test="card-team-member"
                            :data-test-id="sub.accountUid"
                        >
                            <b-row align-v="center">
                                <!-- Member Info -->
                                <b-col cols="12" lg="3" class="d-flex align-items-center mb-3 mb-lg-0">
                                    <b-avatar
                                        :src="sub.picture"
                                        :text="getInitials(sub.fullName || sub.email)"
                                        size="56px"
                                        variant="primary"
                                        class="mr-3 flex-shrink-0"
                                    ></b-avatar>
                                    <div>
                                        <div class="d-flex align-items-center flex-wrap">
                                            <span class="team-member-name font-weight-bold mr-2">{{ sub.fullName }}</span>
                                            <b-badge v-if="sub.isFormerMember" variant="secondary" class="px-2 py-1" data-test="badge-former-member">
                                                Former Member
                                            </b-badge>
                                        </div>
                                        <div class="small text-muted mb-1">{{ sub.email || getMemberRole(sub) }}</div>
                                        <div class="d-flex flex-wrap align-items-center gap-1">
                                            <b-badge variant="light" class="border px-2 py-1 mr-1">
                                                <i class="bi bi-clock-history mr-1"></i>{{ sub.dailyCapacity }}h / day
                                            </b-badge>
                                            <b-badge :variant="getInsightBadgeVariant(sub)" class="px-2 py-1 mr-1">
                                                {{ insightCaption(sub) }}
                                            </b-badge>
                                            <b-button
                                                v-if="isCurrentTeamAdmin && !sub.isFormerMember"
                                                variant="outline-primary"
                                                size="sm"
                                                class="py-0 px-2 extra-small mt-1"
                                                data-test="btn-nudge-member"
                                                title="Send email & in-app timesheet reminder"
                                                @click="openNudgeModal(sub, false)"
                                            >
                                                <i class="bi bi-bell mr-1"></i>Remind
                                            </b-button>
                                        </div>
                                    </div>
                                </b-col>

                                <!-- Progress & Breakdown -->
                                <b-col cols="12" lg="5" class="mb-3 mb-lg-0">
                                    <div class="time-progress-card team-progress-card">
                                        <div class="time-progress-title">Logged Monthly Progress</div>
                                        <time-progress-item
                                            :label="isCurrentMonthDisplayed ? 'This Month' : 'Month'"
                                            period="month"
                                            :period-date="selectedMonth"
                                            :marker-date="today"
                                            :period-start-date="sub.startLogDate"
                                            :daily-capacity="sub.dailyCapacity"
                                            :value="monthLoggedHours(sub)"
                                            :to-date-value="monthToDateHours(sub)"
                                            :future-value="monthFutureHours(sub)"
                                            :total="monthTotalHours(sub)"
                                        />
                                        <div class="team-insight-chips">
                                            <span class="team-chip team-chip-work">Work {{ formatLoggedHours(workHours(sub)) }} hrs</span>
                                            <span class="team-chip team-chip-activity">Activity {{ formatLoggedHours(activityHours(sub)) }} hrs</span>
                                            <span class="team-chip team-chip-absence">Absence {{ formatLoggedHours(absenceHours(sub)) }} hrs</span>
                                        </div>
                                    </div>
                                </b-col>

                                <!-- Doughnut Chart -->
                                <b-col cols="12" lg="4">
                                    <div class="time-progress-card team-chart-card">
                                        <worktype-doughnut :hours="memberHours(sub)" title="Breakdown hours by type"/>
                                    </div>
                                </b-col>
                            </b-row>
                        </b-card>
                        <template #overlay>
                            <load-spinner text="Loading team members..." />
                        </template>
                    </b-overlay>
                </div>
            </b-card>

            <!-- Timesheet Reminder / Nudge Modal (Email + In-App Notification) -->
            <b-modal
                id="modal-send-nudge"
                v-model="showNudgeModal"
                centered
                hide-footer
                :title="isBulkNudge ? 'Remind All Members Behind Target' : 'Send Timesheet Reminder'"
            >
                <div data-test="modal-send-nudge">
                    <div class="alert alert-info small py-2 px-3 mb-3">
                        <i class="bi bi-bell-fill mr-1"></i>
                        Delivers both an <strong>Email</strong> with a direct link to Timelog and a dismissible <strong>In-App Banner</strong> when the member logs in.
                    </div>

                    <div v-if="isBulkNudge" class="mb-3">
                        <label class="small font-weight-bold text-secondary d-block mb-1">
                            Recipients Behind Target ({{ membersBehindList.length }}):
                        </label>
                        <div class="d-flex flex-wrap">
                            <b-badge
                                v-for="m in membersBehindList"
                                :key="m.accountUid"
                                variant="light"
                                class="border mr-1 mb-1 px-2 py-1"
                            >
                                {{ m.fullName }} ({{ Math.max(0, Math.round(monthTotalHours(m) - monthLoggedHours(m))) }}h missing)
                            </b-badge>
                        </div>
                    </div>
                    <div v-else-if="nudgeTargetMember" class="mb-3">
                        <div class="small text-muted">Recipient:</div>
                        <div class="font-weight-bold">
                            {{ nudgeTargetMember.fullName }}
                            <span class="text-muted font-weight-normal">({{ nudgeTargetMember.email }})</span>
                        </div>
                        <div class="extra-small text-muted mt-1">
                            Period: <strong>{{ monthLabel }}</strong> &bull;
                            Logged: <strong>{{ monthLoggedHours(nudgeTargetMember) }}h</strong> / Expected: <strong>{{ monthTotalHours(nudgeTargetMember) }}h</strong>
                        </div>
                    </div>

                    <b-form-group label="Reminder Message" label-class="small font-weight-bold">
                        <b-form-textarea
                            v-model="nudgeMessage"
                            rows="3"
                            placeholder="Please log or reconcile your remaining hours so we can finalize the sprint metrics."
                            data-test="input-nudge-message"
                        ></b-form-textarea>
                    </b-form-group>

                    <div class="d-flex justify-content-end border-top pt-3 mt-3">
                        <b-button variant="outline-secondary" size="sm" class="mr-2" data-test="btn-cancel-nudge" @click="showNudgeModal = false">
                            Cancel
                        </b-button>
                        <b-button
                            variant="primary"
                            size="sm"
                            class="px-3 font-weight-bold"
                            :disabled="sendingNudge"
                            data-test="btn-confirm-send-nudge"
                            @click="sendNudgeAsync"
                        >
                            <b-spinner v-if="sendingNudge" small class="mr-1"></b-spinner>
                            <i v-else class="bi bi-send-fill mr-1"></i>Send Reminder
                        </b-button>
                    </div>
                </div>
            </b-modal>
        </b-container>
    </div>
</template>

<script>
    import accountMixin from "../mixinAccount";
    import mixinToast from "../mixinToast";
    import workTypeChartMixin from "../mixinWorkTypeChart";
    import WorktypeDoughnut from "./chart-worktype-doughnut.vue";
    import TimeProgressItem from "./time-progress-item.vue";
    import CardMissingSource from "./card-missing-source.vue";
    import { format } from "date-fns";

    export default {
        name: "PageTeamInsights",
        mixins: [accountMixin, mixinToast, workTypeChartMixin],
        components: {
            "worktype-doughnut": WorktypeDoughnut,
            "time-progress-item": TimeProgressItem,
            "card-missing-source": CardMissingSource
        },
        data() {
            return {
                loadingAll: false,

                // Team & Project Space Filter Selection
                selectedWorkspaceId: null,
                selectedProjectSpaceId: null,

                // Dual-Channel Nudge Modal State
                showNudgeModal: false,
                isBulkNudge: false,
                nudgeTargetMember: null,
                nudgeMessage: "",
                sendingNudge: false,

                // Profiles Data
                subordinates: [],
                loadingSubordinates: true,
                selectedMonth: this.startOfMonth(new Date()),
                memberSearchQuery: ""
            };
        },
        computed: {
            availableWorkspaces() {
                return (this.workspaces || []).filter(w => !w.isArchived);
            },
            activeTeamObj() {
                if (!this.selectedWorkspaceId) {
                    return this.availableWorkspaces.length ? this.availableWorkspaces[0] : null;
                }
                return this.availableWorkspaces.find(w => w.workspaceId === this.selectedWorkspaceId) || null;
            },
            activeTeamProjectSpaces() {
                const team = this.activeTeamObj;
                if (!team) return [];
                return team.linkedProjectSpaces || team.sharedProjectSpaces || [];
            },
            isCurrentTeamAdmin() {
                const role = this.activeTeamObj?.currentUserRole;
                if (role !== undefined && role !== null) {
                    return role === 0 || role === "0" || role === "Owner" || role === 1 || role === "1" || role === "Admin";
                }
                return false;
            },
            currentTeamRoleLabel() {
                return this.formatTeamRole(this.activeTeamObj?.currentUserRole);
            },
            membersBehindList() {
                return (this.subordinates || []).filter(sub => {
                    if (sub.isFormerMember) return false;
                    const logged = this.monthLoggedHours(sub);
                    const total = this.monthTotalHours(sub);
                    return total > 0 && logged < total;
                });
            },
            monthLabel() {
                return this.selectedMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
            },
            isCurrentMonthDisplayed() {
                const now = this.startOfMonth(new Date());
                return this.selectedMonth.getFullYear() === now.getFullYear()
                    && this.selectedMonth.getMonth() === now.getMonth();
            },
            today() {
                const now = new Date();
                return new Date(now.getFullYear(), now.getMonth(), now.getDate());
            },
            filteredSubordinates() {
                if (!this.memberSearchQuery) return this.subordinates;
                const query = this.memberSearchQuery.toLowerCase().trim();
                return this.subordinates.filter(sub => {
                    const name = (sub.fullName || "").toLowerCase();
                    const email = (sub.email || "").toLowerCase();
                    const role = this.getMemberRole(sub).toLowerCase();
                    return name.includes(query) || email.includes(query) || role.includes(query);
                });
            },
            profilesSummary() {
                let totalExpected = 0;
                let totalLogged = 0;
                let work = 0;
                let activity = 0;
                let absence = 0;

                for (const sub of this.subordinates) {
                    totalExpected += this.monthTotalHours(sub);
                    totalLogged += this.monthLoggedHours(sub);
                    work += this.workHours(sub);
                    activity += this.activityHours(sub);
                    absence += this.absenceHours(sub);
                }

                const completionPct = totalExpected > 0 ? Math.round((totalLogged / totalExpected) * 1000) / 10 : 0;
                return {
                    totalExpected: Math.round(totalExpected),
                    totalLogged: Math.round(totalLogged),
                    workHours: Math.round(work),
                    activityHours: Math.round(activity),
                    absenceHours: Math.round(absence),
                    completionPct
                };
            },
            isServiceUnavailable() {
                return !!this.$store.state.isServiceUnavailable;
            }
        },
        watch: {
            async isDevEnv(newVal) {
                if (newVal) {
                    this.loadingAll = true;
                    await this.loadTeamSessionDataAsync();
                    await this.loadTeamInsights();
                    this.loadingAll = false;
                }
            },
            "$route.query.workspaceId"(wsId) {
                if (wsId && wsId !== this.selectedWorkspaceId) {
                    this.selectedWorkspaceId = wsId;
                    this.selectedProjectSpaceId = null;
                    this.loadTeamInsights();
                }
            }
        },
        async created() {
            if (!this.isDevEnv) {
                this.loadingAll = false;
                this.loadingSubordinates = false;
                return;
            }
            this.loadingAll = true;
            await this.loadTeamSessionDataAsync();
            const queryWsId = this.$route.query.workspaceId;
            if (queryWsId && this.availableWorkspaces.some(w => w.workspaceId === queryWsId)) {
                this.selectedWorkspaceId = queryWsId;
            } else if (this.availableWorkspaces.length) {
                this.selectedWorkspaceId = this.availableWorkspaces[0].workspaceId;
            } else {
                this.selectedWorkspaceId = null;
            }
            await this.loadTeamInsights();
            this.loadingAll = false;
        },
        methods: {
            formatTeamRole(role) {
                if (role === 0 || role === "0" || role === "Owner") return "Owner";
                if (role === 1 || role === "1" || role === "Admin") return "Admin";
                if (role === 2 || role === "2" || role === "Member") return "Member";
                if (role === 3 || role === "3" || role === "ReadOnly" || role === "Guest") return "Read-Only";
                return "Member";
            },
            onTeamSelectionChange() {
                this.selectedProjectSpaceId = null;
                if (this.activeTeamObj) {
                    this.$store.commit("setWorkspace", this.activeTeamObj);
                }
                this.loadTeamInsights();
            },
            onProjectSpaceSelectionChange() {
                this.loadTeamInsights();
            },
            goToManageTeams() {
                this.$router.push({
                    path: "/config",
                    query: { section: "teams" }
                }).catch(() => {});
            },
            openNudgeModal(member = null, isBulk = false) {
                this.isBulkNudge = !!isBulk;
                this.nudgeTargetMember = member;
                this.nudgeMessage = `Please log or reconcile your remaining hours for ${this.monthLabel} so we can finalize the sprint metrics.`;
                this.showNudgeModal = true;
            },
            async sendNudgeAsync() {
                const wsId = this.selectedWorkspaceId || this.activeTeamObj?.workspaceId || this.workspace?.workspaceId;
                if (!wsId) return;

                const targets = this.isBulkNudge
                    ? this.membersBehindList
                    : (this.nudgeTargetMember ? [this.nudgeTargetMember] : []);

                if (!targets.length) {
                    this.showNudgeModal = false;
                    return;
                }

                this.sendingNudge = true;
                try {
                    for (const m of targets) {
                        const missing = Math.max(0, Math.round((this.monthTotalHours(m) - this.monthLoggedHours(m)) * 10) / 10);
                        await this.$http.post("/TeamReport/Nudge", {
                            workspaceId: wsId,
                            recipientAccountUid: m.accountUid,
                            periodLabel: this.monthLabel,
                            missingHours: missing,
                            message: this.nudgeMessage
                        });
                    }
                    this.toastSuccess(
                        "Reminder Sent",
                        targets.length > 1
                            ? `Sent email and in-app timesheet reminders to ${targets.length} squad members.`
                            : `Sent email and in-app timesheet reminder to ${targets[0].fullName}.`
                    );
                    this.showNudgeModal = false;
                } catch (err) {
                    this.toastError("Reminder Failed", err.response?.data?.message || "Could not send timesheet reminder.");
                } finally {
                    this.sendingNudge = false;
                }
            },
            buildTeamFilterParams(extra = {}) {
                const params = { ...extra };
                if (this.selectedWorkspaceId) {
                    params.workspaceId = this.selectedWorkspaceId;
                }
                if (this.selectedProjectSpaceId) {
                    params.projectSpaceId = this.selectedProjectSpaceId;
                }
                return params;
            },
            async loadTeamInsights() {
                if (!this.isTeamOrHigher) {
                    this.subordinates = this.getMockSubordinates();
                    this.loadingSubordinates = false;
                    return;
                }
                this.loadingSubordinates = true;
                try {
                    const resp = await this.$http.get("/AccountConfig/ListSubordinates", {
                        params: this.buildTeamFilterParams({
                            date: format(this.selectedMonth, "yyyy-MM-dd")
                        })
                    });
                    this.subordinates = resp.data && resp.data.length ? resp.data : this.getMockSubordinates();
                } catch (err) {
                    if (err?.status !== 402 && err?.response?.status !== 402) {
                        console.warn("Failed to load team insights, falling back to mock dataset", err);
                    }
                    this.subordinates = this.getMockSubordinates();
                } finally {
                    this.loadingSubordinates = false;
                }
            },
            startOfMonth(date) {
                return new Date(date.getFullYear(), date.getMonth(), 1);
            },
            navigateMonths(delta) {
                const next = this.startOfMonth(new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth() + delta, 1));
                const now = this.startOfMonth(new Date());
                if (delta > 0 && next > now) return;
                this.selectedMonth = next;
                this.loadTeamInsights();
            },
            getMemberRole(sub) {
                const map = {
                    "Alex Morgan": "Tech Lead & Full-Stack",
                    "Sarah Connor": "Lead Frontend Engineer",
                    "John Reese": "DevOps & Cloud Platform",
                    "Elena Rostova": "QA Automation Lead",
                    "Marcus Vance": "Senior Database Specialist",
                    "Chloe Price": "Junior Frontend Developer"
                };
                return map[sub.fullName] || sub.role || "Software Engineer";
            },
            getInitials(name) {
                if (!name) return "U";
                const parts = name.trim().split(" ");
                if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                return name.substring(0, 2).toUpperCase();
            },
            getInsightBadgeVariant(sub) {
                const logged = this.monthLoggedHours(sub);
                const total = this.monthTotalHours(sub);
                if (!total) return "secondary";
                if (logged >= total * 0.9 && logged <= total * 1.15) return "success";
                if (logged < total * 0.9) return "warning";
                return "info";
            },
            summaryOf(sub) {
                return sub?.summary || {};
            },
            summaryNumber(sub, ...keys) {
                const summary = this.summaryOf(sub);
                for (const key of keys) {
                    if (summary[key] != null) return Number(summary[key]) || 0;
                }
                return 0;
            },
            monthLoggedHours(sub) {
                return this.summaryNumber(sub, "monthLoggedHours", "thisMonthToDateLoggedHours");
            },
            monthToDateHours(sub) {
                return this.summaryNumber(sub, "monthToDateHours", "monthLoggedHours", "thisMonthToDateLoggedHours");
            },
            monthFutureHours(sub) {
                return this.summaryNumber(sub, "monthFutureHours");
            },
            monthTotalHours(sub) {
                return this.summaryNumber(sub, "monthTotalHours", "thisMonthToDateTotalHours");
            },
            memberHours(sub) {
                const summary = this.summaryOf(sub);
                return summary.monthByWorkTypeHours || summary.thisMonthToDateByWorkTypeHours || [];
            },
            hoursByLogType(sub, logType) {
                const wanted = (logType || "").toLowerCase();
                return (this.memberHours(sub) || []).reduce((sum, item) => {
                    return (item.logType || "").toLowerCase() === wanted ? sum + (Number(item.hours) || 0) : sum;
                }, 0);
            },
            workHours(sub) {
                return this.summaryNumber(sub, "workHours") || this.hoursByLogType(sub, "Work");
            },
            activityHours(sub) {
                return this.summaryNumber(sub, "activityHours") || this.hoursByLogType(sub, "Activity");
            },
            absenceHours(sub) {
                return this.summaryNumber(sub, "absenceHours") || this.hoursByLogType(sub, "Absence");
            },
            insightCaption(sub) {
                const logged = this.monthLoggedHours(sub);
                const total = this.monthTotalHours(sub);
                if (!total) return "No expected hours";
                if (logged === 0) return "No hours logged";
                if (logged < total) return "Behind expected";
                if (logged === total) return "On track";
                return "Ahead / Over";
            },
            getMockSubordinates() {
                return [
                    {
                        accountUid: "11111111-1111-1111-1111-111111111111",
                        fullName: "Alex Morgan",
                        email: "alex.morgan@velocytrack.com",
                        role: "Tech Lead & Full-Stack",
                        picture: "",
                        dailyCapacity: 8,
                        startLogDate: new Date().toISOString(),
                        summary: {
                            monthLoggedHours: 152,
                            thisMonthToDateLoggedHours: 152,
                            monthToDateHours: 152,
                            monthFutureHours: 0,
                            monthTotalHours: 160,
                            workHours: 124,
                            activityHours: 20,
                            absenceHours: 8,
                            monthByWorkTypeHours: [
                                { logType: "Work", workType: "Development", hours: 100 },
                                { logType: "Work", workType: "Architecture & API", hours: 24 },
                                { logType: "Activity", workType: "Code Review", hours: 20 },
                                { logType: "Absence", workType: "Public Holiday", hours: 8 }
                            ]
                        }
                    },
                    {
                        accountUid: "22222222-2222-2222-2222-222222222222",
                        fullName: "Sarah Connor",
                        email: "sarah.connor@velocytrack.com",
                        role: "Lead Frontend Engineer",
                        picture: "",
                        dailyCapacity: 8,
                        startLogDate: new Date().toISOString(),
                        summary: {
                            monthLoggedHours: 148,
                            thisMonthToDateLoggedHours: 148,
                            monthToDateHours: 148,
                            monthFutureHours: 0,
                            monthTotalHours: 160,
                            workHours: 120,
                            activityHours: 20,
                            absenceHours: 8,
                            monthByWorkTypeHours: [
                                { logType: "Work", workType: "Frontend UI / UX", hours: 100 },
                                { logType: "Work", workType: "Components & Styling", hours: 20 },
                                { logType: "Activity", workType: "Design System Sync", hours: 20 },
                                { logType: "Absence", workType: "Sick Leave", hours: 8 }
                            ]
                        }
                    },
                    {
                        accountUid: "33333333-3333-3333-3333-333333333333",
                        fullName: "John Reese",
                        email: "john.reese@velocytrack.com",
                        role: "DevOps & Cloud Platform",
                        picture: "",
                        dailyCapacity: 6,
                        startLogDate: new Date().toISOString(),
                        summary: {
                            monthLoggedHours: 114,
                            thisMonthToDateLoggedHours: 114,
                            monthToDateHours: 114,
                            monthFutureHours: 0,
                            monthTotalHours: 120,
                            workHours: 96,
                            activityHours: 12,
                            absenceHours: 6,
                            monthByWorkTypeHours: [
                                { logType: "Work", workType: "CI/CD & DevOps", hours: 68 },
                                { logType: "Work", workType: "Docker & Kubernetes", hours: 28 },
                                { logType: "Activity", workType: "Infra Standup", hours: 12 },
                                { logType: "Absence", workType: "Training / KubeCon", hours: 6 }
                            ]
                        }
                    },
                    {
                        accountUid: "44444444-4444-4444-4444-444444444444",
                        fullName: "Elena Rostova",
                        email: "elena.rostova@velocytrack.com",
                        role: "QA Automation Lead",
                        picture: "",
                        dailyCapacity: 8,
                        startLogDate: new Date().toISOString(),
                        summary: {
                            monthLoggedHours: 136,
                            thisMonthToDateLoggedHours: 136,
                            monthToDateHours: 136,
                            monthFutureHours: 0,
                            monthTotalHours: 160,
                            workHours: 104,
                            activityHours: 16,
                            absenceHours: 16,
                            monthByWorkTypeHours: [
                                { logType: "Work", workType: "Automated QA & E2E", hours: 84 },
                                { logType: "Work", workType: "Regression Verification", hours: 20 },
                                { logType: "Activity", workType: "Quality Gate Review", hours: 16 },
                                { logType: "Absence", workType: "Vacation / PTO", hours: 16 }
                            ]
                        }
                    },
                    {
                        accountUid: "55555555-5555-5555-5555-555555555555",
                        fullName: "Marcus Vance",
                        email: "marcus.vance@velocytrack.com",
                        role: "Senior Database Specialist",
                        picture: "",
                        dailyCapacity: 8,
                        startLogDate: new Date().toISOString(),
                        summary: {
                            monthLoggedHours: 160,
                            thisMonthToDateLoggedHours: 160,
                            monthToDateHours: 160,
                            monthFutureHours: 0,
                            monthTotalHours: 160,
                            workHours: 136,
                            activityHours: 16,
                            absenceHours: 8,
                            monthByWorkTypeHours: [
                                { logType: "Work", workType: "PostgreSQL & EF Core", hours: 104 },
                                { logType: "Work", workType: "Query Optimization", hours: 32 },
                                { logType: "Activity", workType: "Sprint Review", hours: 16 },
                                { logType: "Absence", workType: "Public Holiday", hours: 8 }
                            ]
                        }
                    },
                    {
                        accountUid: "66666666-6666-6666-6666-666666666666",
                        fullName: "Chloe Price",
                        email: "chloe.price@velocytrack.com",
                        role: "Junior Frontend Developer",
                        picture: "",
                        dailyCapacity: 8,
                        startLogDate: new Date().toISOString(),
                        summary: {
                            monthLoggedHours: 92,
                            thisMonthToDateLoggedHours: 92,
                            monthToDateHours: 92,
                            monthFutureHours: 0,
                            monthTotalHours: 160,
                            workHours: 72,
                            activityHours: 20,
                            absenceHours: 0,
                            monthByWorkTypeHours: [
                                { logType: "Work", workType: "Component Styling", hours: 56 },
                                { logType: "Work", workType: "Bug Fixes", hours: 16 },
                                { logType: "Activity", workType: "Mentorship Sync", hours: 20 }
                            ]
                        }
                    }
                ];
            }
        }
    };
</script>

<style scoped>
    .config-section-card {
        background-color: var(--vt-card-bg, #ffffff) !important;
        border-color: var(--vt-card-border, #dee2e6) !important;
        border-radius: 12px;
        overflow: hidden;
        transition: box-shadow 0.2s ease;
    }

    .config-section-card:hover {
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05) !important;
    }

    .config-section-card .card-header {
        background-color: var(--vt-card-bg, #ffffff) !important;
        border-color: var(--vt-border, #dee2e6) !important;
    }

    .config-section-card .card-header h5 {
        color: var(--vt-text, #212529) !important;
    }

    .team-page-header {
        border-bottom-color: var(--vt-border) !important;
    }

    .team-title {
        color: var(--vt-text);
    }

    .team-month-label {
        min-width: 10rem;
        text-align: center;
        color: var(--vt-text);
    }

    .team-member-card {
        background: var(--vt-card-bg);
        border: 1px solid var(--vt-border) !important;
        border-radius: 0.75rem;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
    }

    .team-member-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
    }

    .team-member-name {
        font-size: 1.15rem;
        color: var(--vt-text);
    }

    .time-progress-card {
        background: var(--vt-card-bg);
        border: 1px solid var(--vt-border);
        border-radius: 0.75rem;
        padding: 1rem 1.1rem;
        height: 100%;
    }

    .team-progress-card,
    .team-chart-card {
        min-height: 16rem;
    }

    .time-progress-title {
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--vt-muted);
        margin-bottom: 0.85rem;
        text-align: center;
    }

    .team-insight-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.95rem;
    }

    .team-chip {
        display: inline-flex;
        align-items: center;
        border-radius: 999px;
        padding: 0.25rem 0.7rem;
        font-size: 0.78rem;
        font-weight: 600;
    }

    .team-chip-work {
        background: rgba(40, 167, 69, 0.15);
        color: #28a745;
        border: 1px solid rgba(40, 167, 69, 0.25);
    }

    .team-chip-activity {
        background: rgba(13, 110, 253, 0.15);
        color: #0d6efd;
        border: 1px solid rgba(13, 110, 253, 0.25);
    }

    .team-chip-absence {
        background: rgba(111, 66, 193, 0.15);
        color: #6f42c1;
        border: 1px solid rgba(111, 66, 193, 0.25);
    }

    .stat-card {
        background: var(--vt-card-bg);
        border: 1px solid var(--vt-border) !important;
        border-radius: 12px;
        transition: transform 0.2s ease;
    }

    .stat-card:hover {
        transform: translateY(-2px);
    }

    .team-coming-soon-card {
        background: var(--vt-card-bg, #ffffff);
        border: 1px solid var(--vt-border, rgba(0, 0, 0, 0.12)) !important;
        border-radius: 16px;
    }

    .coming-soon-icon-badge {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        background: rgba(13, 110, 253, 0.1);
        color: #0d6efd;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 2.2rem;
    }

    [data-theme="dark"] .coming-soon-icon-badge {
        background: rgba(13, 110, 253, 0.22);
        color: #60a5fa;
    }

    .coming-soon-pill {
        letter-spacing: 0.5px;
        font-size: 0.72rem;
    }

    .feature-preview-card {
        background: var(--vt-card-bg, #f8f9fa);
        border: 1px solid var(--vt-border, rgba(0, 0, 0, 0.08));
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .feature-preview-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    }

    [data-theme="dark"] .feature-preview-card {
        background: rgba(255, 255, 255, 0.03);
        border-color: rgba(255, 255, 255, 0.08);
    }

    @media (max-width: 767.98px) {
        .team-management-page {
            padding-top: 0.75rem !important;
            padding-bottom: 1.5rem !important;
        }
        .team-page-header {
            margin-bottom: 0.85rem !important;
            padding-bottom: 0.65rem !important;
        }
        .team-title {
            font-size: 1.25rem !important;
        }
        .team-month-label {
            min-width: auto;
            font-size: 0.9rem;
        }
        .team-profiles-section {
            padding: 0.75rem !important;
        }
        .team-progress-card,
        .team-chart-card {
            min-height: auto;
        }
    }
</style>
